
import { Component, toNative, Vue } from 'vue-facing-decorator';
import SideDrawer from '@/components/Common/SideDrawer.vue';
import ModalPopup from '@/components/Common/ModalPopup.vue';
import { FetchData } from '@/main';
import GroupItems from '@/entities/Participants/GroupItems';


@Component({
  components: {
    SideDrawer,
    ModalPopup,
  },
})
class GroupsListView extends Vue {
  private addIcon = require('@/assets/Images/white-add-icon.svg');
  private dropDownIcon = require('@/assets/Images/down-arrow-white.svg');
  private deletePopupIcon = require('@/assets/Images/Delete-popup-icon.svg');
  private openMenuId: number | null = null;
  private dropdownAbove = false;
  private groupsListingData: GroupItems[] = [];

  // ── Create manual drawer state ─────────────────────────────────────
  private showCreateManualDrawer = false;
  private manualGroupNames: string[] = [''];

  // ── Create auto drawer state ───────────────────────────────────────
  private showCreateAutoDrawer = false;
  private autoGroupPrefix = '';
  private autoNumberOfGroups = 1;
  private totalParticipants = 50;

  private isCreating = false;

  // ── Edit drawer state ──────────────────────────────────────────────
  private showEditDrawer = false;
  private editGroupName = '';
  private editFilterableInApp = false;
  private editingGroupId: number | null = null;

  // ── Delete modal state ─────────────────────────────────────────────
  private showModalPopup = false;
  private modalPopupHeading = '';
  private modalPopupBodyHeading = '';
  private modalPopupBodyContent = '';
  private modalPopupShowCancelButton = false;
  private modalPopupCancelButtonText = '';
  private modalPopupConfirmButtonText = '';
  private showCloseButton = false;
  private pendingDeleteGroupId: number | null = null;

  private get HasValidManualGroupName(): boolean {
    return this.manualGroupNames.some((n) => n.trim() !== '');
  }

  private get participantsPerGroup(): number {
    if (this.autoNumberOfGroups < 1) { return 0; }
    return Math.floor(this.totalParticipants / this.autoNumberOfGroups);
  }

  private mounted(){
    this.GetGroupsInformation();
    document.addEventListener('click', this.CloseAllMenus);
  }

  private beforeUnmount() {
    document.removeEventListener('click', this.CloseAllMenus);
  }

  // Fetch Groups listing Data
  private GetGroupsInformation(){
    FetchData('/EventAttendee/GetEventGroups','').then((response)=>{
      this.groupsListingData = response;
    }).catch(()=>{
      // console log error if any
    })
  }

  private CloseAllMenus() {
    this.openMenuId = null;
  }

  private OnToggleFilterable(group: GroupItems) {
    group.IsFilterableInApp = !group.IsFilterableInApp;
    // TODO: FetchData('/Groups/UpdateFilterable', JSON.stringify({ id: group.id, filterableInApp: group.filterableInApp }))
  }

  private ToggleGroupMenu(id: number, event: MouseEvent) {
    if (this.openMenuId === id) {
      this.openMenuId = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.dropdownAbove = spaceBelow < 150;
    this.openMenuId = id;
  }

  // ── Create manual drawer ───────────────────────────────────────────
  private OpenCreateManualDrawer() {
    this.manualGroupNames = [''];
    this.showCreateManualDrawer = true;
  }

  private AddAnotherGroup() {
    this.manualGroupNames.push('');
  }

  private RemoveManualGroupEntry(index: number) {
    this.manualGroupNames.splice(index, 1);
  }

  private ConfirmCreateManual() {
    const validNames = this.manualGroupNames.filter((n) => n.trim() !== '');
    if (validNames.length === 0) {
      return;
    }
    this.isCreating = true;
    this.CreateManualGroupSequentially(validNames, 0);
  }

  private CreateManualGroupSequentially(names: string[], index: number) {
    if (index >= names.length) {
      this.isCreating = false;
      this.showCreateManualDrawer = false;
      this.GetGroupsInformation();
      return;
    }
    FetchData('/Participant/EditParticipantGroup', JSON.stringify({
      participantGroupdto: { Id: 0, Name: names[index].trim(), ParticipantCounts: 0 },
    })).then(() => {
      this.CreateManualGroupSequentially(names, index + 1);
    });
  }

  // ── Create auto drawer ─────────────────────────────────────────────
  private OpenCreateAutoDrawer() {
    this.autoGroupPrefix = '';
    this.autoNumberOfGroups = 1;
    this.showCreateAutoDrawer = true;
  }

  private ConfirmCreateAuto() {
    if (this.autoNumberOfGroups < 1) {
      return;
    }
    this.isCreating = true;
    this.CreateAutoGroupSequentially(1);
  }

  private CreateAutoGroupSequentially(current: number) {
    if (current > this.autoNumberOfGroups) {
      this.isCreating = false;
      this.showCreateAutoDrawer = false;
      this.GetGroupsInformation();
      return;
    }
    const name = this.autoGroupPrefix.trim()
      ? `${this.autoGroupPrefix.trim()} ${current}`
      : `Group ${current}`;
    FetchData('/Participant/EditParticipantGroup', JSON.stringify({
      participantGroupdto: { Id: 0, Name: name, ParticipantCounts: 0 },
    })).then(() => {
      this.CreateAutoGroupSequentially(current + 1);
    });
  }

  // ── Edit drawer ────────────────────────────────────────────────────
  private OpenEditDrawer(group: GroupItems) {
    this.editingGroupId = group.Id;
    this.editGroupName = group.Name;
    this.editFilterableInApp = group.IsFilterableInApp;
    this.openMenuId = null;
    this.showEditDrawer = true;
  }

  private ConfirmEdit() {
    const group = this.groupsListingData.find((g) => g.Id === this.editingGroupId);
    if (group) {
      group.Name = this.editGroupName.trim();
      group.IsFilterableInApp = this.editFilterableInApp;
    }
    this.showEditDrawer = false;
    // TODO: FetchData('/Groups/UpdateGroup', JSON.stringify({ id: this.editingGroupId, name: this.editGroupName, filterableInApp: this.editFilterableInApp }))
  }

  // ── Delete modal ───────────────────────────────────────────────────
  private ShowDeletePopup(group: GroupItems) {
    this.openMenuId = null;
    this.pendingDeleteGroupId = group.Source === 'Manual' ? group.Id : null;
    this.modalPopupHeading = this.$t('Groups.DeleteTitle').toString();
    this.modalPopupBodyHeading = '';
    this.modalPopupBodyContent = group.Source === 'Manual' ? this.$t('Groups.DeleteBody').toString() : this.$t('Groups.DeleteUsedInSelection').toString();
    this.modalPopupShowCancelButton = group.Source === 'Manual' ? true : false;
    this.modalPopupCancelButtonText = this.$t('CancelButton').toString();
    this.modalPopupConfirmButtonText = group.Source === 'Manual' ? this.$t('Button.Delete').toString() : this.$t('Button.Ok').toString();
    this.showCloseButton = true;
    this.showModalPopup = true;
  }

  private ConfirmClick(confirmed: boolean) {
    this.showModalPopup = false;
    if (confirmed && this.pendingDeleteGroupId !== null) {
      this.DeleteGroup(this.pendingDeleteGroupId);
    }
    this.pendingDeleteGroupId = null;
  }

  private DeleteGroup(groupId: number) {
    FetchData('/Participant/DeleteParticipantGroup', JSON.stringify({ groupId }))
      .then(() => {
        this.groupsListingData = this.groupsListingData.filter((g) => g.Id !== groupId);
      });
  }
}

export default toNative(GroupsListView);
